/**
 * Confirmation dialog for rolling back to a prior snapshot. We use shadcn
 * `Dialog` with explicit Confirm / Cancel buttons rather than `alert-dialog`
 * (which isn't shipped in `components/ui/`).
 *
 * Wires `useRollbackSnapshot` (B7) into the rollback CTA. On success the
 * snapshot cache and the annotation cache are auto-invalidated by the hook
 * itself, so the queue refreshes without any extra plumbing here.
 */
import React from 'react';
import { toast } from 'sonner';

import { Button } from '../../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import { useRollbackSnapshot } from '../../hooks/useSnapshots';
import type { Snapshot } from '../../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The snapshot to roll back to. Null when the dialog is closed. */
  snapshot: Snapshot | null;
}

const RollbackConfirmDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  snapshot,
}) => {
  const rollback = useRollbackSnapshot();

  const handleConfirm = () => {
    if (!snapshot) return;
    rollback.mutate(snapshot.id, {
      onSuccess: () => {
        toast.success(
          'Snapshot restored — affected records reverted to pending',
        );
        onOpenChange(false);
      },
      onError: (err) => {
        toast.error(err.message || 'Rollback failed');
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rollback-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Roll back to a prior snapshot?</DialogTitle>
          <DialogDescription>
            This reverts every non-finalised record to <strong>pending</strong>{' '}
            and flips <code>is_current</code> to false on their current
            annotations and resolutions. <em>Agreed</em> records are reverted
            too (their auto-populated gold labels were computed against the
            taxonomy you're rolling back from, so they need a fresh pass).
            Only <em>finalised</em> records — those a manager has signed off
            into the gold pool — are preserved. Annotation history is kept;
            only the active flags move.
          </DialogDescription>
        </DialogHeader>

        {snapshot && (
          <p
            className="text-[13px] text-foreground"
            data-testid="rollback-target"
          >
            Roll back to{' '}
            <span className="font-mono">{snapshot.version}</span> —{' '}
            <strong>{snapshot.label}</strong>.
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={rollback.isPending}
            data-testid="rollback-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={rollback.isPending || !snapshot}
            data-testid="rollback-confirm"
          >
            {rollback.isPending ? 'Rolling back…' : 'Roll back'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RollbackConfirmDialog;
